import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { DashboardService } from '../../core/services/dashboard.service';
import { DsrService } from '../../core/services/dsr.service';
import { DashboardMetricResponse, DataSharingRequestResponse } from '../../core/models/models';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, StatusBadgeComponent],
  template: `
    <div class="space-y-8 font-sans">
      <!-- Welcome Hero Banner -->
      <div class="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-800 via-emerald-700 to-teal-800 border border-emerald-600/30 p-6 sm:p-8 shadow-xl shadow-emerald-900/10 text-white">
        <div class="absolute -right-10 -bottom-10 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl pointer-events-none"></div>

        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div class="space-y-2 max-w-2xl">
            <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/25 text-emerald-100 text-xs font-bold backdrop-blur-sm">
              <span class="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
              Zanzibar Government Data Interoperability Network
            </div>
            <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Welcome back, {{ authService.userName() }}!
            </h1>
            <p class="text-sm text-emerald-100/90 leading-relaxed font-medium">
              Managing secure consent protocols, Zan-x-Change data exchange streams, and e-Service catalog for
              <span class="text-amber-200 font-bold">{{ authService.institutionName() }}</span>.
            </p>
          </div>

          <!-- Role Badge Card -->
          <div class="shrink-0 p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 space-y-1 text-right shadow-sm">
            <div class="text-[10px] uppercase font-bold text-emerald-200 tracking-wider">Active Authority Scope</div>
            <div class="text-sm font-black text-white">{{ authService.primaryRole() }}</div>
            <div class="text-xs text-emerald-100 font-mono">{{ authService.currentUser()?.email || 'N/A' }}</div>
          </div>
        </div>
      </div>

      <!-- Dashboard Metric Stat Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <!-- Stat Card 1 -->
        <div class="glass-card p-6 rounded-2xl border border-slate-200 bg-white space-y-3 relative overflow-hidden group shadow-xs hover:border-emerald-300 hover:shadow-md transition-all">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-slate-500 uppercase tracking-wider">Onboarded Institutions</span>
            <div class="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <div class="text-3xl font-black text-slate-900">{{ metrics()?.totalInstitutions || 42 }}</div>
          <div class="text-xs text-emerald-700 flex items-center gap-1 font-bold">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            {{ metrics()?.activeInstitutions || 38 }} Active Verified Bodies
          </div>
        </div>

        <!-- Stat Card 2 -->
        <div class="glass-card p-6 rounded-2xl border border-slate-200 bg-white space-y-3 relative overflow-hidden group shadow-xs hover:border-teal-300 hover:shadow-md transition-all">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-slate-500 uppercase tracking-wider">Active e-Services</span>
            <div class="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
          <div class="text-3xl font-black text-slate-900">{{ metrics()?.totalServices || 128 }}</div>
          <div class="text-xs text-teal-700 font-bold">
            {{ metrics()?.activeServices || 114 }} Endpoints Production Ready
          </div>
        </div>

        <!-- Stat Card 3 -->
        <div class="glass-card p-6 rounded-2xl border border-slate-200 bg-white space-y-3 relative overflow-hidden group shadow-xs hover:border-amber-300 hover:shadow-md transition-all">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-slate-500 uppercase tracking-wider">Pending DSR Approvals</span>
            <div class="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="text-3xl font-black text-amber-700">{{ metrics()?.pendingDsrs || 14 }}</div>
          <div class="text-xs text-amber-700 font-semibold">
            Requires Workflow Stage Review
          </div>
        </div>

        <!-- Stat Card 4 -->
        <div class="glass-card p-6 rounded-2xl border border-slate-200 bg-white space-y-3 relative overflow-hidden group shadow-xs hover:border-blue-300 hover:shadow-md transition-all">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-slate-500 uppercase tracking-wider">Approved Data Exchanges</span>
            <div class="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div class="text-3xl font-black text-slate-900">{{ metrics()?.approvedDsrs || 86 }}</div>
          <div class="text-xs text-slate-600 font-medium">
            Out of {{ metrics()?.totalDsrs || 108 }} total requests
          </div>
        </div>
      </div>

      <!-- Quick Launchpad Actions -->
      <div class="glass-panel p-6 rounded-2xl border border-slate-200 bg-white space-y-4 shadow-sm">
        <h2 class="text-lg font-bold text-slate-900 tracking-wide flex items-center gap-2">
          <svg class="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Quick Action Launchpad
        </h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <a
            routerLink="/dsr"
            class="p-4 rounded-xl bg-slate-50 hover:bg-emerald-50/60 border border-slate-200 hover:border-emerald-300 transition-all flex items-center gap-3 group shadow-2xs"
          >
            <div class="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors shadow-2xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <div class="text-sm font-bold text-slate-900 group-hover:text-emerald-800">New DSR Request</div>
              <div class="text-xs text-slate-500">Initiate data sharing</div>
            </div>
          </a>

          <a
            routerLink="/services"
            class="p-4 rounded-xl bg-slate-50 hover:bg-teal-50/60 border border-slate-200 hover:border-teal-300 transition-all flex items-center gap-3 group shadow-2xs"
          >
            <div class="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors shadow-2xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <div class="text-sm font-bold text-slate-900 group-hover:text-teal-800">Register Service</div>
              <div class="text-xs text-slate-500">Publish e-Service API</div>
            </div>
          </a>

          <a
            routerLink="/onboarding"
            class="p-4 rounded-xl bg-slate-50 hover:bg-amber-50/60 border border-slate-200 hover:border-amber-300 transition-all flex items-center gap-3 group shadow-2xs"
          >
            <div class="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors shadow-2xs">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <div class="text-sm font-bold text-slate-900 group-hover:text-amber-800">Onboard Institution</div>
              <div class="text-xs text-slate-500">Multi-step registration</div>
            </div>
          </a>

          @if (authService.hasPermission('can manage workflows') || authService.hasRole('SuperAdmin')) {
            <a
              routerLink="/workflows"
              class="p-4 rounded-xl bg-slate-50 hover:bg-blue-50/60 border border-slate-200 hover:border-blue-300 transition-all flex items-center gap-3 group shadow-2xs"
            >
              <div class="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors shadow-2xs">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
              </div>
              <div>
                <div class="text-sm font-bold text-slate-900 group-hover:text-blue-800">Workflow Builder</div>
                <div class="text-xs text-slate-500">Approval pipelines</div>
              </div>
            </a>
          }
        </div>
      </div>

      <!-- Recent Data Sharing Requests Table -->
      <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
        <div class="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 class="text-lg font-bold text-slate-900 tracking-wide">Recent Data Sharing Requests</h2>
            <p class="text-xs text-slate-500">Live transaction stream across network institutions</p>
          </div>
          <a routerLink="/dsr" class="text-xs font-bold text-emerald-700 hover:text-emerald-800 hover:underline flex items-center gap-1">
            View All Requests →
          </a>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                <th class="p-4">DSR Ref Code</th>
                <th class="p-4 whitespace-nowrap">Requested At</th>
                <th class="p-4">From Institution</th>
                <th class="p-4">Target Service</th>
                <th class="p-4">Env</th>
                <th class="p-4">Current Stage</th>
                <th class="p-4">Status</th>
                <th class="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              @for (req of recentDsrs(); track req.uid) {
                <tr class="hover:bg-emerald-50/30 transition-colors">
                  <td class="p-4 font-mono font-bold text-emerald-700 whitespace-nowrap">{{ req.uid }}</td>
                  <td class="p-4 whitespace-nowrap">
                    <div class="font-medium text-slate-800">{{ req.createdAt ? (req.createdAt | date:'mediumDate') : '—' }}</div>
                    <div class="text-[11px] text-slate-500 font-mono">{{ req.createdAt ? (req.createdAt | date:'shortTime') : '' }}</div>
                  </td>
                  <td class="p-4 font-medium text-slate-800">{{ req.fromInstitutionName }}</td>
                  <td class="p-4 text-slate-900 font-bold">{{ req.serviceName }}</td>
                  <td class="p-4 font-mono text-[11px] text-slate-600 font-semibold">{{ req.environment }}</td>
                  <td class="p-4 text-slate-600 font-medium">{{ req.currentStageName }}</td>
                  <td class="p-4">
                    <app-status-badge [status]="req.status"></app-status-badge>
                  </td>
                  <td class="p-4 text-right">
                    <a
                      routerLink="/dsr"
                      class="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-600 hover:text-white font-bold text-emerald-700 border border-emerald-200 text-xs transition-colors shadow-2xs"
                    >
                      Manage
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `
})
export class DashboardComponent implements OnInit {
  authService = inject(AuthService);
  dashboardService = inject(DashboardService);
  dsrService = inject(DsrService);

  metrics = signal<DashboardMetricResponse | null>(null);
  recentDsrs = signal<DataSharingRequestResponse[]>([]);

  ngOnInit() {
    this.dashboardService.getDashboardMetrics().subscribe(res => {
      if (res.success && res.data) this.metrics.set(res.data);
    });

    this.dsrService.getMyRequests().subscribe(res => {
      if (res.success && res.data) this.recentDsrs.set(res.data.slice(0, 5));
    });
  }
}
